import type { ApplicationContext } from '@hc-frontend/core-context';
import type { IErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import { createSuccessOperationResult } from '@hc-frontend/core-utils-business-operation';
import { onlyDigits } from '@hc-frontend/core-utils-format';

import {
  type HandleContextualTrafficUseCaseParameters,
  Location,
  Prospect,
} from '../../entities';
import type {
  HandleContextualTrafficRepository,
  HandleContextualTrafficUseCasesResult,
} from '../../repositories/handle-contextual-traffic-repository';
import type { UpdateProspectLocationUseCase } from '../update-prospect-location';

export type HandleContextualTrafficUseCase = (
  parameters: HandleContextualTrafficUseCaseParameters,
) => HandleContextualTrafficUseCasesResult;

export type HandleContextualTrafficFactoryParams = Pick<
  ApplicationContext,
  'log'
> & {
  repository: HandleContextualTrafficRepository;
  useLocation: UpdateProspectLocationUseCase;
};

const logOperation = (
  log: ApplicationContext['log'],
  result: IErrorOperationResult<string>,
) => {
  log({
    context: 'server',
    type: 'error',
    message: result.errors[0].code,
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });
};

export function handleContextualTrafficFactory({
  log,
  repository,
  useLocation,
}: HandleContextualTrafficFactoryParams): HandleContextualTrafficUseCase {
  return async ({ sessionId, userId }) => {
    const rawParameters = await repository.getParameters();

    if (!rawParameters.success) {
      logOperation(log, rawParameters);
    } else {
      const safeParameters = Prospect.safeParse(rawParameters.data);

      if (safeParameters.success) {
        const { data } = safeParameters;
        const hasOptions =
          data.applicant ||
          data.spouse ||
          data.children.length > 0 ||
          Object.keys(data.contactInformation).length > 0 ||
          data.householdIncome ||
          data.qualifyingLifeEvent;
        if (hasOptions) {
          const storeResult = await repository.updateProspect(
            {
              ...safeParameters.data,
              contactInformation: {
                ...safeParameters.data.contactInformation,
                phoneNumber: safeParameters.data.contactInformation.phoneNumber
                  ? onlyDigits(
                      safeParameters.data.contactInformation.phoneNumber,
                    )
                  : undefined,
              },
            },
            sessionId,
            userId,
          );

          if (!storeResult.success) {
            logOperation(log, storeResult);
          }
        }
      } else {
        logOperation(log, {
          success: false,
          errors: [
            {
              code: 'failedToHandleContextualTraffic',
              rootCause: safeParameters.error,
            },
          ],
        });
      }
    }

    const rowLocation = await repository.getLocationParameters();

    if (!rowLocation.success) {
      logOperation(log, rowLocation);
    } else if (Object.keys(rowLocation.data).length > 0) {
      const safeLocation = Location.partial()
        .required({ zipCode: true })
        .safeParse(rowLocation.data);

      if (safeLocation.success) {
        await useLocation({
          ...safeLocation.data,
          sessionId,
          userId,
        });
      } else {
        logOperation(log, {
          success: false,
          errors: [
            {
              code: 'failedToHandleContextualTraffic',
              rootCause: safeLocation.error,
            },
          ],
        });
      }
    }

    return createSuccessOperationResult(true);
  };
}
